import { useContext } from 'react'
import { useLatest } from 'ahooks'

import type { IGetHrefPropsOptions } from '@/components/Link/_hooks'
import type { Url } from 'next/dist/shared/lib/router/router'

import { SiteContext } from '@/contexts/SiteContext'
import { IS_DEV } from '@/helpers/env'
import { useGetHrefProps } from '@/components/Link/_hooks'
import { normalizePagePath } from '@/helpers/routes'


import { useRouter as useNextRouter } from 'next/router';
import { useMemo } from 'react';
export const useResolvedHostname = () => {
  const { resolvedHost } = useContext(SiteContext)

  if (!resolvedHost) {
    throw new Error('resolvedHost is none')
  }
  return resolvedHost
}

export const useHostKey = () => {
  const resolvedHost = useResolvedHostname()
  return resolvedHost.hostKey
}

export const useDeepHostKey = () => {
  const resolvedHost = useResolvedHostname()
  return resolvedHost.deepHostKey
}

const FALLBACK_LOCALE = 'en'

export function useLocale() {
  const { locale } = useNextRouter()
  // 如果没有配置 Next.js 的国际化，locale/defaultLocale 均为 undefined
  // 如果访问了除 404/500 的异常页面，比如非法的路由导致的 400 页面（https://github.com/vercel/next.js/blob/8bac76a5b3073319cf8d4449fc8a26e7c8dc6104/test/integration/dynamic-routing/test/index.test.js#L1117-L1120），locale/defaultLocale 也会为 undefined
  // 另外，为了统一的兼容性以及我们通常都只开发英文为主的站点，因此兜底使用 en
  return locale || FALLBACK_LOCALE
}

export function useLocalePath() {
  const locale = useLocale()
  const localeUrl = locale === 'en' ? '' : `/${locale}`
  return localeUrl
}

export const useHostname = () => {
  const resolvedHost = useResolvedHostname()
  return resolvedHost.normalizedHostname
}

export const useDeepHostname = () => {
  const resolvedHost = useResolvedHostname()
  return resolvedHost.normalizedDeepHostname
}

export function useOrigin(): [string, URL] {
  const resolvedHost = useResolvedHostname()
  const url = new URL(resolvedHost.rawOrigin)

  return [url.origin, url]
}

export function useDeepOrigin(): [string, URL] {
  const deepHostName = useDeepHostname()
  const url = new URL(
    IS_DEV ? `http://${deepHostName}` : `https://${deepHostName}`,
  )

  return [url.origin, url]
}

/**
 * 当页面被静态优化时，结合 Next.js 的 rewrites 功能会导致首次加载的 asPath 未被 rewrite，
 * 因此在使用 asPath 属性时，都应该使用该 hook
 *
 * 值得注意的是，asPath 会包含查询参数
 */
export function useAsPath() {
  const { asPath } = useNextRouter()
  const resolvedHost = useResolvedHostname()
  return normalizePagePath(asPath, resolvedHost.pagePrefix)
}

export function useCanonicalHref(): [string, URL] {
  const [origin] = useOrigin()
  const asPath = useAsPath()
  const locale = useLocale()

  const url = new URL(asPath, origin)

  const href =
    url.pathname === '/'
      ? `${url.origin}/${locale}`
      : `${url.origin}/${locale}${url.pathname}`

  return [href, url]
}

export function useUrl() {
  const [, url] = useCanonicalHref()
  return url
}

export interface TransitionOptions
  extends Pick<IGetHrefPropsOptions, 'prefixOrigin'> {
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
  unstable_skipClientCache?: boolean
}

export function useRouter() {
  const router = useNextRouter()
  const getHrefProps = useGetHrefProps()

  const handleRouteChangeRef = useLatest((method: 'push' | 'replace') => {
    const handle: (
      url: Url,
      as?: Url,
      options?: TransitionOptions,
    ) => Promise<boolean> = (href, as, options) => {
      const { hrefProps } = getHrefProps({
        href,
        as,
        locale: options?.locale || router.locale,
        prefixOrigin: options?.prefixOrigin,
      })

      return router[method](hrefProps.href, hrefProps.as, {
        ...options,
        locale: hrefProps.locale,
      })
    }

    return handle
  })

  const result = useMemo(() => {
    return {
      ...router,
      push: handleRouteChangeRef.current('push'),
      replace: handleRouteChangeRef.current('replace'),
    }
  }, [handleRouteChangeRef, router])

  return result
}

/**
 * 当使用该功能时，需要保证页面组件不使用 asPath 作为 key 渲染，即需要显式设置 Page.noPageKey = true
 *
 * 保证页面不会被重新挂载导致页面状态丢失。
 */
export function useRemoveQueryParams() {
  const url = useUrl()
  const router = useRouter()

  const removeQueryParams = useLatest((names: string[]) => {
    names.forEach((item) => {
      url.searchParams.delete(item)
    })

    router.replace(
      {
        pathname: url.pathname,
        query: url.searchParams.toString(),
      },
      undefined,
      {
        shallow: true,
      },
    )
  })

  return { removeQueryParams }
}
